import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DoctorCard from "@/components/DoctorCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, Loader2, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";

export default function Doctors() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [specialty, setSpecialty] = useState(params.get("specialty") || "all");
  const [gender, setGender] = useState("all");
  const [ctype, setCtype] = useState("all");
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data)).catch(() => {});
  }, []);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctors", { params: { q, specialty, gender, consultation_type: ctype } });
      setDoctors(data);
    } catch (e) {
      console.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoctors(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(fetchDoctors, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, specialty, gender, ctype]);

  const content = (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Find a doctor</h1>
        <p className="text-slate-500 mt-1">Search across {specialties.length || "many"} specialties.</p>
      </div>

      <div className="doccare-card p-4 mb-6">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, specialty or clinic" className="pl-9 rounded-xl h-11" data-testid="doctors-search" />
          </div>
          <Select value={specialty} onValueChange={setSpecialty}>
            <SelectTrigger className="rounded-xl h-11" data-testid="filter-specialty"><SelectValue placeholder="Specialty" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All specialties</SelectItem>
              {specialties.map((s) => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger className="rounded-xl h-11" data-testid="filter-gender"><SelectValue placeholder="Gender" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any gender</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ctype} onValueChange={setCtype}>
              <SelectTrigger className="rounded-xl h-11" data-testid="filter-ctype"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any type</SelectItem>
                <SelectItem value="in_person">In-person</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : doctors.length === 0 ? (
        <div className="doccare-card p-10 text-center">
          <Stethoscope className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-3 font-semibold text-slate-900">No doctors match your filters</p>
          <p className="text-sm text-slate-500 mt-1">Try broadening your search or clearing filters.</p>
          <Button variant="outline" className="mt-4 rounded-full" onClick={() => { setQ(""); setSpecialty("all"); setGender("all"); setCtype("all"); }}>Clear filters</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {doctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
        </div>
      )}
    </div>
  );

  // Public + patient can access. If logged in, show shell.
  if (user && user.role === "patient") {
    return <AppShell>{content}</AppShell>;
  }
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
            <span className="text-xl font-bold text-slate-900">DocCare</span>
          </Link>
          <Link to="/login"><Button className="rounded-full" data-testid="doctors-signin">Sign in</Button></Link>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">{content}</div>
    </div>
  );
}
